import type { QueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import type { PaymentMethodVerificationStatusUpdateMessage, UserDto } from '@/queries/user/user.schema';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import Toast from '@/components/ui/Toast/toast';
import { useSocket } from '@/contexts/websockets/websocket-provider';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import { getMarketSourcePriority } from '@/libs/market/market';
import openModal from '@/modals/utils/openModal';
import { QueriesKeys } from '@/queries/queriesKeys';
import { tokenManager } from '@/queries/token-manager';
import { paymentMethodsQueryKey, userMeQueryOptions, useUserMeQuery } from '@/queries/user/user.queries';
import { useAppSelector } from '@/redux/hooks';
import { UserMarketAssignedMessageSchema, VpnDetectedMessageSchema } from './user.schema';

interface PaymentMethodVerificationRequiredMessage {
  userId: string;
  paymentMethodId: string;
}

/**
 * Fetches one authoritative snapshot after the socket becomes ready.
 * TanStack Query deduplicates this with an in-flight users/me request, while
 * staleTime: 0 ensures a completed pre-connect snapshot is reconciled.
 */
export async function reconcileUserMarket(queryClient: QueryClient): Promise<void> {
  try {
    await queryClient.fetchQuery({
      ...userMeQueryOptions,
      staleTime: 0,
    });
  } catch {
    // Keep the last known user snapshot. The normal query error/retry flow
    // remains authoritative, and the next socket connect will reconcile again.
  }
}

export function subscribeToUserMarketReconciliation(socket: Socket, queryClient: QueryClient) {
  const reconcile = () => {
    void reconcileUserMarket(queryClient);
  };

  socket.on('connect', reconcile);

  // The provider can publish an already-connected socket before this listener
  // effect mounts. Reconcile that connection as well as future reconnects.
  if (socket.connected) {
    reconcile();
  }

  return () => {
    socket.off('connect', reconcile);
  };
}

export async function applyUserMarketAssignment(
  queryClient: QueryClient,
  raw: unknown,
  currentUserId: string | null,
): Promise<void> {
  const parsed = UserMarketAssignedMessageSchema.safeParse(raw);
  if (!parsed.success) {
    return;
  }

  let user = queryClient.getQueryData<UserDto>([QueriesKeys.user]);
  const expectedUserId = user?.id ?? currentUserId;

  // Prefer the cached identity: JWT decoding intentionally rejects expired
  // access tokens, while an authenticated socket can remain connected longer.
  if (parsed.data.userId !== expectedUserId) {
    return;
  }

  // An event may beat the initial users/me query. Do not manufacture a partial
  // UserDto; fetch one complete snapshot, then replay the event against it.
  // The request may have started before the assignment, so its response alone
  // is not guaranteed to contain the event we already received.
  if (!user || user.id !== parsed.data.userId) {
    await reconcileUserMarket(queryClient);
    user = queryClient.getQueryData<UserDto>([QueriesKeys.user]);

    if (!user || user.id !== parsed.data.userId) {
      return;
    }
  }

  // A delayed lower-priority assignment cannot downgrade an already more
  // authoritative in-memory snapshot.
  if (getMarketSourcePriority(user.marketSource) > getMarketSourcePriority(parsed.data.marketSource)) {
    return;
  }

  const cachedUpdatedAt = Date.parse(user.updatedAt);
  const eventTimestamp = Date.parse(parsed.data.timestamp);
  if (!Number.isNaN(cachedUpdatedAt) && cachedUpdatedAt > eventTimestamp) {
    return;
  }

  queryClient.setQueryData<UserDto>([QueriesKeys.user], {
    ...user,
    market: parsed.data.market,
    marketSource: parsed.data.marketSource,
    // Backend emits the updated user's timestamp, so this doubles as the
    // ordering watermark for later market events.
    updatedAt: parsed.data.timestamp,
  });
}

export function usePaymentMethodVerificationRequiredSocket() {
  const userSession = useAppSelector(state => state.user.userSession);

  useSocketEvent<PaymentMethodVerificationRequiredMessage>(
    'payment-method.verification-required',
    (data) => {
      openModal('card-verify-request', { paymentMethodId: data.paymentMethodId });
    },
    Boolean(userSession),
  );
}

/**
 * Reconciles a `user.vpn-detection.updated` event: mirrors the flag onto the
 * cached user so `users/me` consumers agree with the socket.
 */
export function applyVpnDetectedMessage(queryClient: QueryClient, raw: unknown): void {
  const parsed = VpnDetectedMessageSchema.safeParse(raw);

  if (!parsed.success) {
    return;
  }

  const { isVpnDetected } = parsed.data;

  queryClient.setQueryData<UserDto>(
    [QueriesKeys.user],
    prev => (prev ? { ...prev, isVpnDetected } : prev),
  );
}

/** VPN modal display is disabled while the detection flag remains reconciled. */
export function shouldOpenVpnModalForUser(
  user: Pick<UserDto, 'isVpnDetected' | 'isTest'> | undefined,
): boolean {
  void user;
  return false;
}

/**
 * Keeps the VPN detection flag reconciled without opening the VPN-detected modal.
 */
export function useVpnDetectedSocket() {
  const userSession = useAppSelector(state => state.user.userSession);
  const queryClient = useQueryClient();
  useUserMeQuery();

  useSocketEvent(
    'user.vpn-detection.updated',
    (data) => {
      applyVpnDetectedMessage(queryClient, data);
    },
    Boolean(userSession),
  );

  // The VPN-detected modal is intentionally not opened from either the socket
  // event or the users/me snapshot while this check is disabled.
}

export function useUserSocket() {
  const userSession = useAppSelector(state => state.user.userSession);
  const t = useTranslations('payment-modals.card-state');
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket || !userSession) {
      return;
    }

    return subscribeToUserMarketReconciliation(socket, queryClient);
  }, [queryClient, socket, userSession]);

  useSocketEvent(
    'user.market.assigned',
    (data) => {
      void applyUserMarketAssignment(queryClient, data, tokenManager.getUserId());
    },
    Boolean(userSession),
  );

  useSocketEvent<PaymentMethodVerificationStatusUpdateMessage>(
    'payment-method-verification.status.update',
    (data) => {
      queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });

      if (data.state === 'approved') {
        Toast({
          type: 'success',
          title: t('card-verification-complete'),
          description: t('ready-for-redemptions'),
        });
        return;
      }

      if (data.state === 'rejected') {
        Toast({
          type: 'error',
          title: t('reject.title'),
          disableTimer: true,
          description: t('reject.description'),
          acceptButtonText: t('reject.accept-button'),
          rejectButtonText: t('reject.reject-button'),
          onAccept: () => {
            window.Intercom?.('show');
          },
        });
      }
    },
    Boolean(userSession),
  );
}
